import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Autocomplete,
  TextField,
  Alert,
  Tabs,
  Tab,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

const ImportZahvalnicaDialog = ({
  open,
  onClose,
  templates,
  selectedTemplate,
  setSelectedTemplate,
  selectedFile,
  setSelectedFile,
  handleImport,
  filterYear,
  setFilterYear,
  getTemplateImageUrl,
  yearWarning,
  zahvalnicaSchools,
  zahvalnicaLoading,
  loading = false,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!open) {
      setFileError('');
      setActiveTab(0);
    }
  }, [open]);

  const currentType = activeTab === 0 ? '1' : '4';
  const filteredTemplates = templates.filter((tmpl) => String(tmpl.type) === currentType);
  const availableYears = [
    'All',
    ...Array.from(new Set(filteredTemplates.map((t) => String(t.year)))),
  ];
  const displayedTemplates =
    filterYear === 'All'
      ? filteredTemplates
      : filteredTemplates.filter((t) => String(t.year) === filterYear);

  const handleTabChange = (_, newTab) => {
    setActiveTab(newTab);
    setSelectedTemplate(null);
    setSelectedFile(null);
    setFileError('');
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!validTypes.includes(file.type) && !['.xlsx', '.xls'].includes(ext)) {
      setFileError(t('importDiploma.fileTypeError'));
      setSelectedFile(null);
      event.target.value = '';
      return;
    }
    setFileError('');
    setSelectedFile(file);
  };

  const headerGradient =
    activeTab === 0
      ? 'linear-gradient(135deg, #f57f17 0%, #f9a825 60%, #ffd54f 100%)'
      : 'linear-gradient(135deg, #2e7d32 0%, #388e3c 60%, #66bb6a 100%)';

  const accentColor = activeTab === 0 ? '#f9a825' : '#388e3c';
  const accentHover = activeTab === 0 ? '#f57f17' : '#2e7d32';
  const selectedBorder = activeTab === 0 ? '2px solid #f9a825' : '2px solid #388e3c';

  const canGenerate = activeTab === 0
    ? Boolean(selectedFile && selectedTemplate)
    : Boolean(selectedTemplate);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { position: 'relative', overflow: 'hidden' } }}>

      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            borderRadius: 'inherit',
          }}
        >
          <CircularProgress size={56} thickness={4} sx={{ color: accentColor }} />
          <Typography variant="body1" fontWeight={700} sx={{ color: '#333' }}>
            {t('importDiploma.generating')}…
          </Typography>
          <Typography variant="caption" sx={{ color: '#777', textAlign: 'center', px: 4 }}>
            {t('loading.description')}
          </Typography>
        </Box>
      )}

      <Box sx={{ background: headerGradient, px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {activeTab === 0
          ? <StarIcon sx={{ color: '#fff', fontSize: 26 }} />
          : <SchoolIcon sx={{ color: '#fff', fontSize: 26 }} />
        }
        <Box>
          <Typography variant="h6" fontWeight={700} color="#fff">
            {t('importZahvalnica.dialogTitle')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.88)' }}>
            {activeTab === 0
              ? t('importZahvalnica.zzSubtitle')
              : t('importZahvalnica.zSubtitle')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: '#fafafa' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '14px' },
            '& .Mui-selected': { color: accentColor },
            '& .MuiTabs-indicator': { backgroundColor: accentColor },
          }}
        >
          <Tab
            icon={<StarIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Chip label="ZZ" size="small"
                  sx={{ backgroundColor: '#fff8e1', color: '#f57f17', fontWeight: 800, fontSize: '11px', border: '1px solid #ffe082', height: 20 }} />
                {t('importZahvalnica.zzTabLabel')}
              </Box>
            }
          />
          <Tab
            icon={<SchoolIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Chip label="Z" size="small"
                  sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 800, fontSize: '11px', border: '1px solid #a5d6a7', height: 20 }} />
                {t('importZahvalnica.zTabLabel')}
              </Box>
            }
          />
        </Tabs>
      </Box>

      <DialogContent>
        <Alert
          icon={<InfoOutlinedIcon />}
          severity="info"
          sx={{ mb: 2, borderRadius: '8px', backgroundColor: activeTab === 0 ? '#fff8e1' : '#e8f5e9', border: `1px solid ${activeTab === 0 ? '#ffe082' : '#a5d6a7'}`, color: activeTab === 0 ? '#856404' : '#1b5e20' }}
        >
          {activeTab === 0 ? (
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                {t('importZahvalnica.zzExplanationTitle')}
              </Typography>
              <Typography variant="body2">{t('importZahvalnica.zzExplanationBody')}</Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                {t('importZahvalnica.zExplanationTitle')}
              </Typography>
              <Typography variant="body2">{t('importZahvalnica.zExplanationBody')}</Typography>
            </Box>
          )}
        </Alert>

        {yearWarning && (
          <Alert severity="warning" icon={<WarningAmberIcon />}
            sx={{ mb: 2, borderRadius: '8px', border: '1px solid #ffc107' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('importZlatna.yearConflictTitle', { year: yearWarning.year })}
            </Typography>
            <Typography variant="body2">
              {t('importZlatna.yearConflictBody', { count: yearWarning.count })}
            </Typography>
          </Alert>
        )}

        {filteredTemplates.length === 0 && (
          <Box sx={{ p: 2, mb: 2, backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px' }}>
            <Typography variant="body2" sx={{ color: '#856404', fontWeight: 'bold' }}>
              {t('importZlatna.noTemplatesTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#856404' }}>
              {t('importZlatna.addTemplateFirst')}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Autocomplete
            size="small"
            options={availableYears}
            value={filterYear || 'All'}
            onChange={(_, v) => setFilterYear(v || 'All')}
            getOptionLabel={(option) => option === 'All' ? t('general.all') : option.toString()}
            renderInput={(params) => <TextField {...params} label={t('diplomaTemplate.year')} />}
            sx={{ minWidth: 140 }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {displayedTemplates.map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Card
                onClick={() => !loading && setSelectedTemplate(template)}
                sx={{
                  cursor: loading ? 'default' : 'pointer',
                  border: selectedTemplate?.id === template.id ? selectedBorder : '1px solid #ddd',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: loading ? 0 : 4 },
                }}
              >
                <CardMedia component="img" height="140" image={getTemplateImageUrl(template)} alt={template.name} />
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5, color: '#1a237e', fontSize: '14px' }}>
                    {template.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333', fontWeight: '600' }}>
                    {t('diplomaTemplate.yearLabel')} {template.year || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: accentColor, fontWeight: '600' }}>
                    {t('diplomaTemplate.typeLabel')}{' '}
                    {activeTab === 0 ? t('diplomaTemplate.zlatna') : t('diplomaTemplate.zahvalnica')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {activeTab === 0 && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('importDiploma.fileWarning')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('importZlatna.instruction')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                onClick={() => fileInputRef.current.click()}
                variant="contained"
                disabled={!selectedTemplate || loading}
                title={!selectedTemplate ? t('importDiploma.selectTemplateFirst') : ''}
                startIcon={<UploadFileIcon />}
                sx={{
                  backgroundColor: selectedTemplate && !loading ? accentColor : '#bdbdbd',
                  color: '#fff',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: selectedTemplate && !loading ? accentHover : '#bdbdbd' },
                }}
              >
                {t('importDiploma.selectFile')}
              </Button>

              {!selectedTemplate && (
                <Typography variant="caption" sx={{ color: '#d32f2f' }}>
                  {t('importDiploma.selectTemplateFirst')}
                </Typography>
              )}
              {fileError && (
                <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}>
                  {fileError}
                </Typography>
              )}
              {selectedFile && (
                <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  ✓ {selectedFile.name}
                </Typography>
              )}
              <input type="file" ref={fileInputRef} hidden accept=".xlsx,.xls" onChange={handleFileChange} />
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            {!selectedTemplate && (
              <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 1 }}>
                {t('importDiploma.selectTemplateFirst')}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button onClick={onClose} disabled={loading}>{t('dialog.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => handleImport({ tab: activeTab, template: selectedTemplate, file: selectedFile })}
            disabled={!canGenerate || loading}
            sx={{
              backgroundColor: (!canGenerate || loading)
                ? '#bdbdbd'
                : yearWarning ? '#e65100' : accentColor,
              color: '#fff',
              fontWeight: 'bold',
              borderRadius: '8px',
              minWidth: 160,
              '&:hover': {
                backgroundColor: (!canGenerate || loading)
                  ? '#bdbdbd'
                  : yearWarning ? '#bf360c' : accentHover,
              },
            }}
          >
            {loading
              ? <CircularProgress size={20} thickness={4} sx={{ color: '#fff' }} />
              : activeTab === 0
                ? (yearWarning ? t('importZlatna.replaceAndGenerate') : t('importZlatna.generate'))
                : (yearWarning ? t('importZahvalnica.replaceAndGenerate') : t('importZahvalnica.generate'))
            }
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ImportZahvalnicaDialog;